import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService } from '../../../../shared/services/api.service';
import { AuthService } from '../../../../shared/services/auth.service';
import { DateService } from '../../../../shared/services/date.service';

export interface ESimDetail {
  id: number;
  created_at: string;
  iccid: string;
  lpa: string;
  imsis: string | null;
  matching_id: string;
  qrcode: string;
  qrcode_url: string;
  direct_apple_installation_url: string;
  voucher_code: string | null;
  airalo_code: string | null;
  apn_type: string;
  apn_value: string | null;
  is_roaming: boolean;
  confirmation_code: string | null;
  brand_settings_name: string | null;
  msisdn: string | null;
  recycled: boolean;
  recycled_at: string | null;
  apn: {
    ios: {
      apn_type: string;
      apn_value: string | null;
    };
    android: {
      apn_type: string;
      apn_value: string | null;
    };
  };
  sharing: {
    link: string;
    access_code: string;
  };
  // Additional fields that might be available in the detailed response
  usage?: any;
  package_history?: any[];
  installation_instructions?: string;
}

export interface InstructionsResponse {
  error: boolean;
  data: any; // This will contain the installation instructions
  message: string;
}

export interface ESimDetailResponse {
  error: boolean;
  data: {
    data: ESimDetail;
  };
  message: string;
}

@Component({
  standalone: false,
  selector: 'app-sim-details',
  templateUrl: './sim-details.component.html',
  styleUrl: './sim-details.component.scss'
})
export class SimDetailsComponent implements OnInit {
  sim: ESimDetail | null = null;
  loading = true;
  error: string | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private apiService: ApiService,
    private authService: AuthService,
    private _dateService: DateService
  ) {}

  ngOnInit(): void {
    const iccid = this.route.snapshot.paramMap.get('iccid');
    if (iccid) {
      this.loadSimDetails(iccid);
    } else {
      this.error = 'SIM ICCID not found';
      this.loading = false;
    }
  }

  loadSimDetails(iccid: string): void {
    // The JWT interceptor will automatically add the token header
    this.apiService.getData(`/api/airalo/sims/${iccid}`).subscribe({
      next: (response: ESimDetailResponse) => {
        if (!response.error && response.data && response.data.data) {
          this.sim = response.data.data;
        } else {
          this.error = 'Failed to load SIM details';
        }
        this.loading = false;
      },
      error: (err) => {
        console.error('Error fetching SIM details:', err);
        this.error = 'Error loading SIM details';
        this.loading = false;
      }
    });
  }

  goBack(): void {
    this.router.navigate(['/app/sims']);
  }

  viewUsage(): void {
    const iccid = this.route.snapshot.paramMap.get('iccid');
    if (iccid) {
      this.router.navigate(['/app/sims', iccid, 'usage']);
    }
  }

  viewPackageHistory(): void {
    const iccid = this.route.snapshot.paramMap.get('iccid');
    if (iccid) {
      this.router.navigate(['/app/sims', iccid, 'packages']);
    }
  }

  viewInstructions(): void {
    const iccid = this.route.snapshot.paramMap.get('iccid');
    if (iccid) {
      // Navigate to the instructions page
      this.router.navigate(['/app/sims', iccid, 'instructions']);
    }
  }

  viewCompatibleDevices(): void {
    this.router.navigate(['/app/compatible-devices']);
  }

  copyToClipboard(text: string): void {
    navigator.clipboard.writeText(text).then(() => {
      console.log('Copied to clipboard');
    }).catch(err => {
      console.error('Could not copy text: ', err);
    });
  }

  formatDate(dateValue: string): string {
    return this._dateService.getFormattedDateWithAMPM(dateValue);
  }
}